import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { formatRmb } from '../../lib/marginPreview.js';

const EMPTY_ENTITY = Object.freeze({
  name: '海外主体 1',
  profit_share_pct: 100,
  operating_expense_rmb: 0,
  income_tax_pct: 0
});

const EMPTY_PROFILE = Object.freeze({
  name: '默认主体配置',
  country_region: '',
  currency: 'CNY',
  overseas_profit_share_pct: 30,
  china_income_tax_pct: 25,
  domestic_operating_expense_rmb: 0,
  overseas_entities: [{ ...EMPTY_ENTITY }],
  is_default: true
});

function pct(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : '—';
}

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cloneProfile(profile = {}) {
  const entities = Array.isArray(profile.overseas_entities) && profile.overseas_entities.length > 0
    ? profile.overseas_entities
    : EMPTY_PROFILE.overseas_entities;
  return {
    ...EMPTY_PROFILE,
    ...profile,
    overseas_entities: entities.map((entity, idx) => ({
      name: entity?.name || `海外主体 ${idx + 1}`,
      profit_share_pct: toNumber(entity?.profit_share_pct ?? (entities.length === 1 ? 100 : 0)),
      operating_expense_rmb: toNumber(entity?.operating_expense_rmb),
      income_tax_pct: toNumber(entity?.income_tax_pct ?? entity?.tax_pct)
    }))
  };
}

function Kpi({ label, value, hint }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-2 text-xl font-semibold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-600">{hint}</div>}
    </div>
  );
}

function Field({ label, value, onChange, type = 'number', suffix, hint }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type={type}
          className="field"
          value={value}
          step={type === 'number' ? '0.01' : undefined}
          min={type === 'number' ? '0' : undefined}
          max={type === 'number' && suffix === '%' ? '100' : undefined}
          onChange={(e) => onChange(type === 'number' ? toNumber(e.target.value) : e.target.value)}
        />
        {suffix && <span className="w-8 text-sm text-slate-700">{suffix}</span>}
      </div>
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

export default function EntityProfitCenter() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(cloneProfile());
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/entities/summary?limit=100');
      setData(res);
      const first = res?.default_profile || res?.profiles?.[0] || null;
      if (first) {
        setActiveId(first.id);
        setForm(cloneProfile({ ...first, is_default: true }));
      }
    } catch (err) {
      if (err instanceof HttpError && err.status === 403) {
        setError('当前套餐未开通跨境主体模块。');
      } else {
        setError(err?.message || '加载跨境主体模块失败');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rows = data?.items || [];
  const kpis = data?.kpis || {};
  const profiles = data?.profiles || [];
  const profileMap = useMemo(() => new Map(profiles.map((p) => [p.id, p])), [profiles]);
  const overseasShareTotal = form.overseas_entities.reduce(
    (acc, entity) => acc + toNumber(entity.profit_share_pct), 0
  );

  const selectProfile = (id) => {
    const p = profileMap.get(Number(id));
    if (!p) {
      setActiveId(null);
      setForm(cloneProfile());
      return;
    }
    setActiveId(p.id);
    setForm(cloneProfile({ ...p, is_default: p.is_default }));
  };

  const patchEntity = (idx, patch) => {
    setForm((f) => ({
      ...f,
      overseas_entities: f.overseas_entities.map((entity, i) =>
        i === idx ? { ...entity, ...patch } : entity
      )
    }));
  };

  const addEntity = () => {
    setForm((f) => ({
      ...f,
      overseas_entities: [
        ...f.overseas_entities,
        { ...EMPTY_ENTITY, name: `海外主体 ${f.overseas_entities.length + 1}`, profit_share_pct: 0 }
      ]
    }));
  };

  const removeEntity = (idx) => {
    setForm((f) => {
      if (f.overseas_entities.length <= 1) return f;
      return {
        ...f,
        overseas_entities: f.overseas_entities.filter((_, i) => i !== idx)
      };
    });
  };

  const saveProfile = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, is_default: true };
      const saved = activeId
        ? await api.patch(`/api/entities/profiles/${activeId}`, payload)
        : await api.post('/api/entities/profiles', payload);
      setActiveId(saved.id);
      await load();
    } catch (err) {
      setError(err?.message || '保存主体配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">跨境主体</h1>
          <p className="mt-1 text-sm text-slate-700">
            承接外贸/电商扣点后的利润，拆分国内利润、海外主体利润、中国税和海外税。
          </p>
        </div>
        <Link to="/app/quotes/new" className="btn-primary">新建带主体口径的报价</Link>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-4">
        <Kpi label="主体报价数" value={loading ? '—' : kpis.quotes_with_entity || 0} />
        <Kpi label="进入主体前利润" value={formatRmb(kpis.profit_before_entity_rmb)} />
        <Kpi label="中国税 + 海外税" value={formatRmb(
          Number(kpis.china_income_tax_rmb || 0) + Number(kpis.overseas_tax_cost_rmb || 0)
        )} />
        <Kpi label="集团净利率" value={pct(kpis.group_net_margin_pct)} />
      </div>

      <div className="grid gap-5 lg:grid-cols-[460px_1fr]">
        <section className="rounded-md border border-slate-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-slate-900">主体配置</h2>
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => { setActiveId(null); setForm(cloneProfile()); }}
            >
              新建配置
            </button>
          </div>

          {profiles.length > 0 && (
            <label className="mb-4 block">
              <span className="label">已有配置</span>
              <select
                className="field"
                value={activeId || ''}
                onChange={(e) => selectProfile(e.target.value)}
              >
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.is_default ? ' · 默认' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="配置名称" type="text" value={form.name}
              onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
            <Field label="国家/地区备注" type="text" value={form.country_region}
              onChange={(v) => setForm((f) => ({ ...f, country_region: v }))} />
            <label className="block">
              <span className="label">主体币种</span>
              <select
                className="field"
                value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
              >
                <option value="CNY">CNY</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="JPY">JPY</option>
                <option value="GBP">GBP</option>
              </select>
            </label>
            <Field label="海外主体利润占比" suffix="%" value={form.overseas_profit_share_pct}
              hint={`国内利润占比 ${(100 - toNumber(form.overseas_profit_share_pct)).toFixed(2)}%`}
              onChange={(v) => setForm((f) => ({ ...f, overseas_profit_share_pct: v, transfer_pricing_pct: v }))} />
            <Field label="中国所得税率" suffix="%" value={form.china_income_tax_pct}
              onChange={(v) => setForm((f) => ({ ...f, china_income_tax_pct: v }))} />
            <Field label="国内运营费用" value={form.domestic_operating_expense_rmb}
              onChange={(v) => setForm((f) => ({ ...f, domestic_operating_expense_rmb: v }))} />
          </div>

          <div className="mt-5 rounded-md border border-slate-200 p-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">海外主体</h3>
                <p className="text-xs text-slate-600">至少一个；多个主体时，按分配占比拆海外利润。</p>
              </div>
              <button type="button" className="btn-ghost text-sm" onClick={addEntity}>+ 添加</button>
            </div>
            <div className="space-y-3">
              {form.overseas_entities.map((entity, idx) => (
                <div key={idx} className="rounded-md border border-slate-100 p-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="主体名称" type="text" value={entity.name}
                      onChange={(v) => patchEntity(idx, { name: v })} />
                    <Field label="海外内部分配" suffix="%" value={entity.profit_share_pct}
                      onChange={(v) => patchEntity(idx, { profit_share_pct: v })} />
                    <Field label="运营费用" value={entity.operating_expense_rmb}
                      onChange={(v) => patchEntity(idx, { operating_expense_rmb: v })} />
                    <Field label="海外所得税率" suffix="%" value={entity.income_tax_pct}
                      onChange={(v) => patchEntity(idx, { income_tax_pct: v })} />
                  </div>
                  <button type="button"
                    className="mt-2 text-xs text-rose-600 hover:text-rose-800 disabled:text-slate-300"
                    disabled={form.overseas_entities.length <= 1}
                    onClick={() => removeEntity(idx)}>
                    删除主体
                  </button>
                </div>
              ))}
            </div>
            <p className={`mt-2 text-xs ${Math.abs(overseasShareTotal - 100) > 0.01 ? 'text-amber-700' : 'text-slate-600'}`}>
              海外主体分配占比合计：{overseasShareTotal.toFixed(2)}%。建议合计 100%。
            </p>
          </div>

          <button
            type="button"
            className="btn-primary mt-4 w-full"
            disabled={saving}
            onClick={saveProfile}
          >
            {saving ? '保存中…' : '保存为默认主体配置'}
          </button>
          <p className="mt-3 text-xs leading-5 text-slate-600">
            配置用于复用主体参数；每张报价保存后，主体拆分以报价当时的快照为准。
          </p>
        </section>

        <section className="rounded-md border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h2 className="text-base font-semibold text-slate-900">主体利润快照</h2>
            <span className="text-sm text-slate-600">最近 {rows.length} 条</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px] text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium text-slate-700">
                <tr>
                  <th className="px-4 py-3">报价</th>
                  <th className="px-4 py-3">客户</th>
                  <th className="px-4 py-3 text-right">主体前利润</th>
                  <th className="px-4 py-3 text-right">国内利润</th>
                  <th className="px-4 py-3 text-right">中国税</th>
                  <th className="px-4 py-3 text-right">海外利润</th>
                  <th className="px-4 py-3 text-right">海外运营费</th>
                  <th className="px-4 py-3 text-right">海外税</th>
                  <th className="px-4 py-3 text-right">集团净利</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-600">加载中…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-slate-600">
                      暂无带 E 层参数的报价。进入 <Link className="text-chango-700 underline" to="/app/quotes/new">新建报价</Link> 后填写主体口径即可沉淀快照。
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={`/app/quotes/${row.id}`} className="font-medium text-chango-700 hover:underline">
                        {row.code}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-800">{row.customer_name || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRmb(row.profit_before_entity_rmb)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRmb(row.domestic_profit_before_tax_rmb)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRmb(row.china_income_tax_rmb)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRmb(row.overseas_profit_before_tax_rmb)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRmb(row.overseas_operating_expense_rmb)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatRmb(row.overseas_tax_cost_rmb)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">{formatRmb(row.group_net_profit_rmb)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
